import React from "react";

interface ProjectLink {
  label: string;
  href: string;
  primary?: boolean;
}

interface Project {
  name: string;
  description: string;
  badge?: string;
  preview?: {
    style: React.CSSProperties;
    icon: string;
    label: string;
    href: string;
    lightLabel?: boolean;
  };
  links: ProjectLink[];
}

const projects: Project[] = [
  {
    name: "Design System",
    description:
      "A living component library and visual language reference. Colours, typography, buttons, inputs, badges, cards, and spacing tokens.",
    preview: {
      style: {
        background: "linear-gradient(135deg, #F7F3EE 0%, #E8E4DF 100%)",
        border: "1px solid #E8E4DF",
      },
      icon: "🎨",
      label: "View System",
      href: "/design-system",
    },
    links: [
      { label: "🎨 View Design System", href: "/design-system", primary: true },
      { label: "GitHub", href: "https://github.com/xhokcu/design-system" },
    ],
  },
  {
    name: "DevBoard",
    description:
      "A minimal Kanban board built for developers. Designed and built end-to-end in Figma and React. Organize tasks, track progress, and ship faster.",
    preview: {
      style: {
        background: "linear-gradient(135deg, #1B2B4B 0%, #2d4470 100%)",
      },
      icon: "🗂️",
      label: "Go to App",
      href: "https://devboard-io.netlify.app",
      lightLabel: true,
    },
    links: [
      {
        label: "🗂️ Live App",
        href: "https://devboard-io.netlify.app",
        primary: true,
      },
      {
        label: "🎨 Figma",
        href: "https://www.figma.com/design/FZvwOe4O0JXZYIYHtgmVVJ/DevBoard?node-id=0-1&t=7NcGsZ0QjmlQxeeS-1",
      },
      { label: "GitHub", href: "https://github.com/xhokcu/devboard" },
    ],
  },
  {
    name: "Sweet Sprint",
    description:
      "A 2D side-scrolling browser game designed and built from scratch using JavaScript and p5.js. Every character, background, and game mechanic was designed by me.",
    preview: {
      style: {
        background: "linear-gradient(135deg, #6EC6E6 0%, #E8903A 100%)",
      },
      icon: "▶",
      label: "Play now",
      href: "https://sweet-sprint.netlify.app",
      lightLabel: true,
    },
    links: [
      {
        label: "🍩 Play the Game",
        href: "https://sweet-sprint.netlify.app",
        primary: true,
      },
      { label: "GitHub", href: "https://github.com/xhokcu/sweet-sprint" },
    ],
  },
  {
    name: "Tick Task",
    badge: "Coming soon",
    description:
      "A productivity-focused mobile app built with React Native, Firebase, and Firestore. Designed to help users manage daily tasks in a clean, intuitive interface.",
    links: [
      { label: "🔗 Landing Page", href: "https://ticktask-app.netlify.app" },
      { label: "GitHub", href: "https://github.com/xhokcu/tick-task" },
    ],
  },
];

function Projects() {
  return (
    <div className="py-14 border-b border-light-gray">
      <div className="flex items-center gap-3 mb-8">
        <span className="text-[11px] tracking-[0.12em] uppercase text-warm-gray font-semibold">
          Projects
        </span>
        <div className="flex-1 h-px bg-light-gray" />
      </div>

      <div className="flex flex-col gap-10">
        {projects.map((project) => (
          <div key={project.name} className="flex flex-col gap-3">
            <h2 className="font-display font-bold text-navy text-lg flex items-center gap-3">
              {project.name}
              {project.badge && (
                <span className="text-[11px] font-sans font-normal text-warm-gray">
                  {project.badge}
                </span>
              )}
            </h2>
            <p className="text-[14px] text-text-muted leading-[1.65]">
              {project.description}
            </p>

            {project.preview && (
              <a
                href={project.preview.href}
                target={
                  project.preview.href.startsWith("http") ? "_blank" : undefined
                }
                rel="noopener noreferrer"
                className="relative w-full rounded-xl overflow-hidden cursor-pointer group"
                style={{ ...project.preview.style, aspectRatio: "21/6" }}
              >
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                  <div
                    className="w-13 h-13 rounded-full bg-white/90 flex items-center justify-center shadow-lg transition-transform duration-200 group-hover:scale-110"
                    style={{ width: 52, height: 52 }}
                  >
                    <span className="text-xl">{project.preview.icon}</span>
                  </div>
                  <span
                    className={`text-[11px] font-medium ${project.preview.lightLabel ? "text-white/80" : "text-warm-gray"}`}
                  >
                    {project.preview.label}
                  </span>
                </div>
              </a>
            )}

            <div className="flex gap-2.5">
              {project.links.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target={link.href.startsWith("http") ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  className={`
                    text-[13px] px-4 py-2 rounded-lg font-medium transition-all duration-200
                    inline-flex items-center gap-1.5 border
                    hover:-translate-y-0.5
                    ${
                      link.primary
                        ? "bg-navy text-white border-navy hover:bg-[#243d6b]"
                        : "bg-white text-text border-light-gray hover:border-navy hover:text-navy"
                    }
                  `}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Projects;
